import { ReactNode } from 'react';
import { Skeleton, lucideIcons, tableHelpers } from '@round/creatorbase-ui';
import { ConfigurationItem, getHashtagItems, getSoundItems } from '../../../../../helpers';
import { ConfigurationItemBadge } from '../../../../../components/ConfigurationItemBadge/ConfigurationItemBadge';
import { ConfigurationHoverCard } from '../../components/ConfigurationHoverCard/ConfigurationHoverCard';
import { CellContext, getMeta, getTargetsSummary } from '../../helpers';
import styles from './TargetsCell.module.css';

const { Hash, Music2 } = lucideIcons;

const CARD_TITLE = 'Targets';

export const TargetsCell = ({ row: { original }, table }: CellContext) => {
    const { isLoading, audiosById, getIsAudiosLoading } = getMeta(table);

    if (tableHelpers.isLoadingRow(original) || isLoading) {
        return <Skeleton className={styles.skeleton} />;
    }

    const { audios, hashtags } = original.targets;
    const summary = getTargetsSummary(original.targets);

    if (!audios.length && !hashtags.length) {
        return <span>{summary}</span>;
    }

    const soundItems = getSoundItems(audios, { audiosById, isLoadingAudios: getIsAudiosLoading(original.id) });
    const hashtagItems = getHashtagItems(hashtags);

    return (
        <ConfigurationHoverCard summary={summary} title={CARD_TITLE}>
            <TargetGroup title="Sounds" icon={<Music2 aria-hidden="true" />} items={soundItems} />
            <TargetGroup title="Hashtags" icon={<Hash aria-hidden="true" />} items={hashtagItems} />
        </ConfigurationHoverCard>
    );
};

type TargetGroupProps = {
    title: string;
    icon: ReactNode;
    items: ConfigurationItem[];
};

/** A group with nothing in it is left out rather than heading an empty row. */
const TargetGroup = ({ title, icon, items }: TargetGroupProps) => {
    if (!items.length) {
        return null;
    }

    return (
        <section className={styles.group}>
            <h3 className={styles.groupTitle}>
                {icon}
                {title}
            </h3>

            <div className={styles.groupItems}>
                {items.map((item) => (
                    <ConfigurationItemBadge key={item.key} item={item} />
                ))}
            </div>
        </section>
    );
};
